import React, { useEffect, useState } from "react";
import useCosmosCollections from "../../../hooks/use-cosmos-collections";
import { usePeopleStore } from "../../../stores/use-people-store";
import { fetchPeople } from "../../../api/people-api";
import { useInfiniteQuery, useQueryClient } from "react-query";
import { useOpticsStore } from "../../../stores/use-optics-store";
import useCosmosFields from "../../../hooks/use-cosmos-fields";
import People from "../widget/People";
import { PaginationTypes } from "@Shared/Pagination-Types";
import CustomEmptyState from "../../../components/empty-state/CustomEmptyState";
import { CUSTOM_EMPTY_STATE_KEY } from "../../../components/empty-state/custom-templates";
import { mapUser } from "../mapPeople";

function DynamicPeople({ mode }) {
  const [page, setPage] = useState(0);

  const [collectionsToFetch, setCollectionsToFetch] = useState([]);

  const { config, selectedTab } = usePeopleStore((state) => ({
    config: state.data.config,
    selectedTab: state.getSelectedTab(),
  }));

  const isCosmosInstalled = useOpticsStore((state) => state.isCosmosInstalled);

  const { collections } = useCosmosCollections();
  const { fields } = useCosmosFields();

  const queryClient = useQueryClient();

  const { tabs, variant, pagination, size, titleFieldId, footerFieldId } =
    config || {};

  const { useCollections, selectedCollections, group, limit } =
    tabs[selectedTab] || {};

  const isQueryEnabled = useCollections
    ? !!selectedCollections?.length
    : !!group;

  const { isError, error, data, fetchNextPage, hasNextPage, isFetching } =
    useInfiniteQuery(
      [
        "people",
        {
          useCollections,
          collections: collectionsToFetch,
          group,
          limit,
        },
      ],
      fetchPeople,
      {
        enabled: isQueryEnabled,
        retry: 0,
        select: (response) => {
          if (!response?.pages?.length) return undefined;

          const { pages } = response;

          const builtData = pages.reduce((acc, page, index) => {
            const users =
              page?.data?.results?.map((user) =>
                mapUser(user, {
                  fields,
                  titleFieldId,
                  footerFieldId,
                  isCosmosInstalled,
                })
              ) || [];

            return {
              ...acc,
              [index]: users,
            };
          }, {});

          return builtData;
        },
        getNextPageParam: (lastPage) => {
          const { data } = lastPage || {};

          // handle collections
          if (config?.tabs[selectedTab]?.useCollections) {
            const currentPage = data?.page;
            const totalPages = data?.totalPages;
            if (currentPage >= totalPages) {
              return false;
            }
            return currentPage + 1;
          }

          // handle groups
          const { _links: links } = data || {};
          if (!links?.next) {
            return false;
          }

          const fullNextUrl = new URL(links.base + links.next);
          const nextStart = fullNextUrl.searchParams.get("start");
          return nextStart;
        },
      }
    );

  useEffect(() => {
    if (collections?.length && tabs[selectedTab].selectedCollections?.length) {
      const { selectedCollections } = tabs[selectedTab];
      setCollectionsToFetch(
        collections.filter((c) =>
          selectedCollections.find((clc) => clc === c.data.id)
        )
      );
    } else {
      setCollectionsToFetch([]);
    }
  }, [tabs, selectedTab, collections]);

  useEffect(() => {
    queryClient.resetQueries({ queryKey: "people" });
    setPage(0);
  }, [
    queryClient,
    pagination,
    variant,
    size,
    limit,
    selectedTab,
    useCollections,
    collections,
    group,
  ]);

  const queryPagination = {
    loading: isFetching,
    disabled: config.pagination === PaginationTypes.NONE,
    hasNextPage: hasNextPage || Object.keys(data || {})?.length - 1 > page,
    hasPrevPage: page >= 1,
    onNext: () => {
      if (isFetching) return;
      if (hasNextPage && Object.keys(data || {})?.length - 1 === page) {
        fetchNextPage();
      }
      if (hasNextPage || Object.keys(data || {})?.length - 1 > page) {
        setPage(page + 1);
      }
    },
    onPrev: () => {
      if (page >= 1) {
        setPage(page - 1);
      }
    },
    page,
    setPage,
  };

  if (isError) {
    return <span>Error {error}</span>;
  }

  if (!isQueryEnabled) {
    return (
      <CustomEmptyState templateKey={CUSTOM_EMPTY_STATE_KEY.PEOPLE_SETUP} />
    );
  }

  if (!Object.values(data || {})?.[0]?.length && !isFetching) {
    return (
      <CustomEmptyState
        templateKey={CUSTOM_EMPTY_STATE_KEY.PEOPLE_EMPTY_RESULTS}
      />
    );
  }

  return (
    <People
      data={data}
      queryPagination={queryPagination}
      mode={mode}
      isCosmosInstalled={isCosmosInstalled}
    />
  );
}

export default DynamicPeople;
